// 📄 catalogoApisService.ts
// Catálogo global de APIs "estándar" (SUNAT, SAP Ariba, SAP S/4HANA
// Cloud): plantillas de referencia que gestiona el Admin desde
// /admin > Catálogo de APIs Estándar, para preconfigurar lo que un
// consultor arma luego por cliente en Configuración > APIs (ver
// ConfiguracionApis.tsx / clienteConexionService.ts).
//
// Nunca guardan credenciales (esas son siempre por cliente) — mismo
// modelo que ApiDefinition pero sin apiKey/oauthSecret/usuario/contraseña.
// Reutiliza ApiEndpointConfig/ApiTipoId tal cual porque esa forma ya es
// genérica (sin secretos).
//
// A diferencia de credenciales/configuraciones por cliente (bloqueadas
// del lado del cliente, solo vía onehubApi), esto no es sensible: se lee
// y escribe directo desde Firestore, protegido por firestore.rules
// (isAdmin()) — mismo patrón que organizationService.ts.
import {
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { ApiEndpointConfig, ApiTipoId, ApiAuthTipo } from './clienteConexionService.ts';

export interface ApiTemplateData {
  tipo: ApiTipoId;
  // Modo de autenticación sugerido por esta plantilla — elegible por el
  // Admin (ver radio button en ApiTemplateForm.tsx), independiente de
  // `tipo`: una plantilla Ariba puede ser OAuth2 (la mayoría) o Básica
  // manual (ej. "Flow Extension API", sin token). Al aplicar la plantilla
  // en un proyecto (ver handleAplicarPlantilla en ApiDefinitionForm.tsx)
  // este valor se copia a la API del proyecto.
  authTipo: ApiAuthTipo;
  nombre: string;
  protocolo: string;
  // Código de Data Center ('US' | 'EU' | ''), mismo catálogo que usa
  // ApiDefinitionCard.tsx para las credenciales por cliente (ver
  // components/Configuracion/aribaDataCenters.ts). '' = "Sin especificar
  // / Otro". Reemplaza al viejo campo "ambiente" (Test/PRD), que no tenía
  // relación con la URL Base real de la API.
  dataCenter: string;
  urlBase: string;
  api: string;
  tokenUrl: string;
  grantType: string;
  endpoints: ApiEndpointConfig[];
}

export interface ApiTemplate extends ApiTemplateData {
  id: string;
  createdAt?: any;
  updatedAt?: any;
}

const CATALOGO_COLLECTION = "catalogoApis";

export const getApiTemplates = async (): Promise<ApiTemplate[]> => {
  const snap = await getDocs(collection(db, CATALOGO_COLLECTION));
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      tipo: (data.tipo as ApiTipoId) || 'ariba',
      authTipo: (data.authTipo as ApiAuthTipo) || (data.tipo === 's4hana' ? 'basic' : 'oauth'),
      nombre: data.nombre || '',
      protocolo: data.protocolo || '',
      dataCenter: data.dataCenter || '',
      urlBase: data.urlBase || '',
      api: data.api || '',
      tokenUrl: data.tokenUrl || '',
      grantType: data.grantType || '',
      endpoints: Array.isArray(data.endpoints) ? data.endpoints : [],
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
  });
};

/**
 * Crea o actualiza una plantilla. El id siempre lo genera el cliente de
 * antemano (mismo criterio que ApiDefinition/endpoints, ver
 * ConfiguracionApis.tsx) para poder tener una tarjeta local antes de la
 * primera vez que se guarda — `esNueva` es lo único que distingue "crear"
 * de "actualizar" (para no pisar `createdAt` en cada guardado).
 */
export const saveApiTemplate = async (templateId: string, data: ApiTemplateData, esNueva: boolean): Promise<void> => {
  if (!data.nombre.trim()) throw new Error("El nombre de la plantilla es obligatorio.");

  const payload: Record<string, any> = {
    tipo: data.tipo,
    authTipo: data.authTipo || (data.tipo === 's4hana' ? 'basic' : 'oauth'),
    nombre: data.nombre.trim(),
    protocolo: data.protocolo || '',
    dataCenter: data.dataCenter || '',
    urlBase: data.urlBase || '',
    api: data.api || '',
    tokenUrl: data.tokenUrl || '',
    grantType: data.grantType || '',
    endpoints: data.endpoints || [],
    updatedAt: serverTimestamp()
  };
  if (esNueva) payload.createdAt = serverTimestamp();

  await setDoc(doc(db, CATALOGO_COLLECTION, templateId), payload, { merge: true });
};

export const deleteApiTemplate = async (templateId: string): Promise<void> => {
  await deleteDoc(doc(db, CATALOGO_COLLECTION, templateId));
};

// URL Base estándar del servicio SUNAT: MISMO doc `urls/urlsunat` que ya
// lee functions/services/sunatService.js como fallback global cuando un
// cliente no configuró su propia URL en Configuración > SUNAT (ver
// procesarSunat). Se administra acá en vez de crear una colección nueva
// para no duplicar la fuente de verdad.
const SUNAT_URL_DOC = doc(db, "urls", "urlsunat");

export const getSunatUrlEstandar = async (): Promise<string> => {
  const snap = await getDoc(SUNAT_URL_DOC);
  return snap.exists() ? (snap.data().urlsunat || '') : '';
};

export const saveSunatUrlEstandar = async (url: string): Promise<void> => {
  await setDoc(SUNAT_URL_DOC, { urlsunat: url.trim() }, { merge: true });
};
